/**
 * Spec 200 Fase 6 #4 — applies the persisted font-size preference as
 * a class on `<html>`. The class overrides the `--typo-*` tokens via
 * the rules added to `tokens.css`, so every element that reads a
 * `--typo-*` variable picks up the new size on the next paint.
 *
 * Mirrors the structure of `useTheme` so the two preferences behave
 * identically: mount-read + storage event listener. There is no OS
 * fallback for font size — the user is the only source of truth.
 *
 * Why classes (and not `data-font-size`):
 *   `font-md` / `font-lg` / `font-xl` makes the override scopable in
 *   tests and DevTools (you can hot-toggle without writing JS), and
 *   keeps the attribute selector list in `tokens.css` short.
 */
import { useEffect, useState } from 'react';

import {
  FONT_SIZE_STORAGE_KEY,
  readFontSizePreference,
  type FontSizePreference,
} from './preferences';

const CLASSES: Record<FontSizePreference, string> = {
  small: 'font-sm',
  medium: 'font-md',
  large: 'font-lg',
};
const ALL_CLASSES = Object.values(CLASSES);

function applyToDocument(pref: FontSizePreference): void {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  for (const c of ALL_CLASSES) el.classList.remove(c);
  // 'medium' is the implicit default — leave the class off so the
  // base tokens apply unchanged. This keeps the DOM clean for
  // 99% of users and the override list in CSS minimal.
  if (pref !== 'medium') el.classList.add(CLASSES[pref]);
}

export function useFontSize(): FontSizePreference {
  const [pref, setPref] = useState<FontSizePreference>(() => readFontSizePreference());

  useEffect(() => {
    applyToDocument(pref);
  }, [pref]);

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== null && e.key !== FONT_SIZE_STORAGE_KEY) return;
      setPref(readFontSizePreference());
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return pref;
}
